import * as Sentry from '@sentry/react';

/**
 * Recovers tabs that were loaded before a deploy.
 *
 * Every pod only ships the assets of its own build, so the moment a rollout
 * completes the previous build's hashed chunks return 404. A tab that is still
 * running the old bundle then fails its next dynamic import and the app dies in
 * the error boundary ("Something went wrong"). Reloading fixes it because
 * index.html is served `no-cache`, so this listens for that exact failure and
 * does the reload for the user.
 *
 * The attempt counter guards the pathological case: if the *fresh* build is
 * genuinely missing a chunk, reloading will never help, so after MAX_RELOADS
 * inside RELOAD_WINDOW_MS we stop, report it to Sentry as a real error and let
 * the error boundary render.
 *
 * Keep STORAGE_KEY, RELOAD_WINDOW_MS and MAX_RELOADS in sync with the inline
 * bootstrap snippet in index.html, which handles the case where the entry
 * bundle itself 404s and this module never gets to run.
 */

const STORAGE_KEY = 'scriptix.stale-build-recovery';
const RELOAD_WINDOW_MS = 10 * 60 * 1000;
const MAX_RELOADS = 2;

const STALE_IMPORT_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Loading chunk \S+ failed/i,
  /Importing a module script failed/i,
  /Unable to preload CSS/i,
  /is not a valid JavaScript MIME type/i
];

type RecoveryState = { count: number; firstAt: number };

function readState(): RecoveryState {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { count: 0, firstAt: 0 };
    const parsed = JSON.parse(raw) as Partial<RecoveryState>;
    const count = typeof parsed.count === 'number' ? parsed.count : 0;
    const firstAt = typeof parsed.firstAt === 'number' ? parsed.firstAt : 0;
    // A stale window means the earlier reloads belonged to an older deploy.
    if (Date.now() - firstAt > RELOAD_WINDOW_MS)
      return { count: 0, firstAt: 0 };
    return { count, firstAt };
  } catch {
    // Private mode / disabled storage: behave as if this is the first attempt.
    return { count: 0, firstAt: 0 };
  }
}

function writeState(state: RecoveryState): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Nothing to do — worst case we allow one reload per page load.
  }
}

function reportRecoveryFailure(
  reason: string,
  detail: string,
  reloadCount: number
): void {
  // Only genuine failures are reported. The routine reload path recovers the
  // tab silently — it's not an error and doesn't belong in Sentry Issues.
  // This fires only after MAX_RELOADS, meaning the reload didn't fix it, which
  // is a real bug (fresh build genuinely missing a chunk).
  Sentry.captureMessage('stale-build: asset still missing after reload', {
    level: 'error',
    tags: { stale_build: 'true', stale_build_reason: reason },
    extra: { detail, reloadCount, url: window.location.href }
  });
}

function recover(reason: string, detail: string): void {
  const { count, firstAt } = readState();

  if (count >= MAX_RELOADS) {
    reportRecoveryFailure(reason, detail, count);
    return;
  }

  writeState({ count: count + 1, firstAt: firstAt || Date.now() });
  window.location.reload();
}

function isStaleImportMessage(message: string): boolean {
  return STALE_IMPORT_PATTERNS.some(pattern => pattern.test(message));
}

function isOwnAssetUrl(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.href);
    return (
      parsed.origin === window.location.origin &&
      parsed.pathname.startsWith('/assets/')
    );
  } catch {
    return false;
  }
}

export function installStaleBuildRecovery(): void {
  // Vite's own signal for a failed dynamic import / module preload. Preventing
  // the default stops it from surfacing as an unhandled rejection first.
  window.addEventListener('vite:preloadError', event => {
    event.preventDefault();
    recover('vite:preloadError', event.payload?.message ?? 'unknown');
  });

  window.addEventListener('unhandledrejection', event => {
    const reason = event.reason;
    const message =
      (reason as Error | undefined)?.message ?? String(reason ?? '');
    if (isStaleImportMessage(message)) {
      event.preventDefault();
      recover('unhandledrejection', message);
    }
  });

  // Capture phase: resource load failures don't bubble, and a 404 on a
  // <script>/<link> fires an event with no `error` object.
  window.addEventListener(
    'error',
    event => {
      const target = event.target;
      if (
        target instanceof HTMLScriptElement &&
        isOwnAssetUrl(target.src) &&
        target.src
      ) {
        recover('script-load-failure', target.src);
        return;
      }
      if (
        target instanceof HTMLLinkElement &&
        target.rel === 'stylesheet' &&
        isOwnAssetUrl(target.href)
      ) {
        recover('stylesheet-load-failure', target.href);
        return;
      }
      const message = (event as ErrorEvent).message ?? '';
      if (message && isStaleImportMessage(message)) {
        recover('window-error', message);
      }
    },
    true
  );
}
